import { useBookingService } from '@/hooks/useBookingService';
import { useState } from 'react';
import { Booking, BookingSource, BookingTypes } from '@/types/booking';
import { fromZonedTime } from 'date-fns-tz';
import { sharedConfig } from '@/config/sharedConfig';

interface BookingParams {
  externalId: string;
  userId: string | undefined;
  type: BookingTypes;
  time: Date;
  courseId?: number | undefined;
  partnerPromo?: string;
  locale: string | 'en';
  source: BookingSource;
}

type BookingResponse = Booking | { exceedsLimit: boolean } | null;

export function isSuccessfulBookingResponse(
  response: BookingResponse
): response is Booking {
  return response !== null && 'id' in response;
}

export function isExceedsLimit(
  response: BookingResponse
): response is { exceedsLimit: boolean } {
  return response !== null && 'exceedsLimit' in response;
}

export const useHandleBooking = () => {
  const { createBooking } = useBookingService();
  const [isLoading, setIsLoading] = useState(false);
  const [bookingId, setBookingId] = useState<string | null>(null);
  const [isBookingSuccessful, setIsBookingSuccessful] = useState(false);
  const [usageLimitExceeded, setUsageLimitExceeded] = useState<boolean>(false);

  const handleBooking = async ({
    externalId,
    userId,
    type,
    time,
    courseId,
    partnerPromo,
    locale,
    source,
  }: BookingParams): Promise<BookingResponse> => {
    setIsLoading(true);
    let bookingResponse;
    const timeUtc = fromZonedTime(time, sharedConfig.defaultTimezone);
    try {
      const bookingData = {
        externalId,
        userId: userId || '',
        type: type,
        time: timeUtc,
        courseId: courseId as number,
        partnerPromo,
        locale,
        source,
      };

      bookingResponse = await createBooking(bookingData);

      if (bookingResponse.statusCode !== 201) {
        setUsageLimitExceeded(bookingResponse.statusCode === 402);
        setIsBookingSuccessful(false);
      } else {
        setBookingId(bookingResponse.booking?.id || null);
        setIsBookingSuccessful(true);
      }

      if (bookingResponse.statusCode === 402) {
        return {
          exceedsLimit: true,
        };
      }

      return bookingResponse.booking ?? null;
    } catch (error) {
      console.error('Booking failed:', error);
      return null;
      // alert('Booking failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return {
    handleBooking,
    isLoading,
    bookingId,
    isBookingSuccessful,
    usageLimitExceeded,
  };
};
